import { Link } from 'react-router';

import type { DashboardCampaignDto } from '../../api/dashboard';

import { campaignCards, type StatCard } from './cards';

/**
 * The campaign summary grid (task T.R3) — the owner's correction of the old
 * pill-in-a-card layout: one value per card, display-size, `tabular-nums`, and
 * the label as a small caption above it instead of a count squeezed into a
 * badge. Every card is a real `react-router` link, so the drill-down is
 * keyboard-reachable and middle-clickable for free, and the global
 * `:focus-visible` ring marks the focused one.
 *
 * The model is `campaignCards()` — this file only renders it, so the unit suite
 * pins the labels, codes and hrefs without a DOM.
 *
 * ADDITIVE (T.Z144, v1.9.37) — `format` is the «Тип площадки» axis the page is read on
 * (`kindFormat(kind)`); it reaches the card model so every register drill-in carries it.
 */
export function CampaignCards({
  campaign,
  format,
}: {
  campaign: DashboardCampaignDto | null | undefined;
  format?: 'OFFLINE' | 'ONLINE';
}) {
  return (
    <section className="stat-grid" aria-label="Сводка компании" data-testid="campaign-cards">
      {campaignCards(campaign, format).map((card) => (
        <StatCardTile key={card.testId} card={card} />
      ))}
    </section>
  );
}

/** One card = accent bar, caption, value. A date reads smaller than a count. */
function StatCardTile({ card }: { card: StatCard }) {
  const body = (
    <>
      <span className="stat-card__bar" aria-hidden="true" />
      <span className="stat-card__label">{card.label}</span>
      <span
        className={card.kind === 'snapshot' ? 'stat-card__value stat-card__value--date' : 'stat-card__value'}
        data-testid={card.valueTestId}
      >
        {card.value}
      </span>
    </>
  );
  // ADDITIVE (v1.10.0) — a card with no `href` renders as a plain tile: an
  // informational fact the register answers no exact filter for (the «Материалы не
  // открыты» card). Same chrome, same tooltip, no fake drill.
  if (card.href === undefined) {
    return (
      <div
        className={`stat-card stat-card--${card.accent}`}
        data-testid={card.testId}
        data-kind={card.kind}
        title={card.hint}
      >
        {body}
      </div>
    );
  }
  return (
    <Link
      to={card.href}
      className={`stat-card stat-card--${card.accent}`}
      data-testid={card.testId}
      data-kind={card.kind}
      title={card.hint}
    >
      {body}
    </Link>
  );
}

/**
 * The loading state: nine placeholders of the exact card shape, derived from the
 * model so a card added there shows up here without a second list to maintain.
 */
export function CampaignCardsSkeleton() {
  return (
    <section className="stat-grid" aria-label="Сводка компании" data-testid="campaign-cards-skeleton">
      {campaignCards(undefined).map((card) => (
        <div className="stat-card skeleton" key={card.testId} aria-hidden="true" />
      ))}
    </section>
  );
}
